/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionInputMapping';
import { ActionInvokeDataSourceItem } from '../ActionInvokeDataSource/ActionInvokeDataSource';

const ActionInputMappingManager: ActionManagerFn<'ActionInputMapping'> = (
  platform: Platform,
): ActionManager<'ActionInputMapping'> => {
  let callback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionInputMapping'),
    defaultValue,
    props: {
      ...baseActionProps,
      attribute: {
        label: 'Attribute',
        type: 'InvokeAttributeChooser',
        required: true,
        aliasType: 'STORE',
        getAttributeDataTypes: () => 'ALL',
        getAlias: (page, item, { pageId }) => {
          return {
            alias: (page[item.parent] as ActionInvokeDataSourceItem).props
              .dataSource,
            embeddedPageId: pageId,
          };
        },
        preventMultiEdit: true,
      },
      value: {
        label: 'Value',
        type: 'expression',
        refreshCallback: (cb) => {
          callback = cb;
        },
        returnType: async () => 'any',
        required: true,
        helpText: 'The value must match the data type of the above attribute.',
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'attribute') {
        callback();
        return {
          [property]: value,
          value: defaultValue.value,
        };
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'value':
          return !!props.attribute;
        default:
          return true;
      }
    },
    name: 'Input Mapping',
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return parent.type === 'ActionInvokeDataSource';
    },
    getDisplayText: (item) => item.props.attribute,
    icon: { iconName: 'value-absolute' },
    description: `**Overview**<br/>
The Action Input Mapping component serves as a configuration unit used to map a value to a target attribute. It operates strictly as a child component within an ActionInvokeDataSource context, enabling the transfer of data/arguments into the data source definition.<br/><br/>
This manager handles UI logic for selecting a target attribute (derived from the parent’s data source) and defining the corresponding value using an expression. It enforces configuration integrity by allowing any value type while guiding builders to keep the value aligned with the selected attribute’s data type.

**Key Features**<br/>
* **Parent-Context Dependency:** Explicitly restricted (via isAllowedInsideParent) to function only when nested inside an ActionInvokeDataSource component.
* **Dynamic Attribute Lookup:** The attribute property derives its alias context from the parent component’s dataSource property, ensuring the mapping targets the correct data store entity.
* **State Reset Logic:** When the attribute selection changes, onPropertyChange resets the value property back to its default state (defaultValue.value) to prevent stale/invalid mappings.
* **Sequential Visibility:** The value field remains hidden (via isPropertyVisible) until an attribute is selected, enforcing a valid configuration order.

**Properties**<br/>
* **Attribute (Required):** An InvokeAttributeChooser used to select the target field.
  * *Behavior:* aliasType: 'STORE', preventMultiEdit: true.
  * *Context:* Fetches embeddedPageId and parent data source alias dynamically.
* **Value (Required):** An expression defining the data to assign to the attribute.
  * *Validation:* returnType resolves to 'any'.
  * *Guidance:* Help text indicates the value must match the selected attribute’s data type.

**Use Cases**<br/>
* **Data Source Configuration:** Mapping variables or static values into attributes required by an ActionInvokeDataSource operation.
* **Parameter Passing:** Binding expression-based values to store attributes prior to execution.`,
  };
};

export default ActionInputMappingManager;
